import { useEffect, useMemo, useState } from 'react';
import { ChevronRight, Folder, FolderInput, Search } from 'lucide-react';
import Modal from '../common/Modal';
import { Button } from '../common/Button';
import folderService from '../../services/folderService';
import useAsync from '../../hooks/useAsync';
import cn from '../../utils/cn';

/** Folder picker used by Move and Copy. Descends lazily through the authorized tree. */
export default function MoveDialog({ open, onClose, onSubmit, item, title = 'Move to', confirmLabel = 'Move' }) {
  const [parentId, setParentId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const { data: tree = [] } = useAsync(() => folderService.tree(), [open], { immediate: open });

  useEffect(() => {
    if (open) {
      setParentId(null);
      setSelected(null);
      setQuery('');
      setError(null);
    }
  }, [open]);

  const nodes = tree || [];

  const children = useMemo(() => {
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      return nodes.filter((f) => f.name.toLowerCase().includes(q)).slice(0, 40);
    }
    return nodes.filter((f) => (f.parentId || null) === parentId);
  }, [nodes, parentId, query]);

  const breadcrumb = useMemo(() => {
    const chain = [];
    let current = nodes.find((n) => n.id === parentId);
    let guard = 0;
    while (current && guard < 20) {
      chain.unshift(current);
      current = nodes.find((n) => n.id === current.parentId);
      guard += 1;
    }
    return chain;
  }, [nodes, parentId]);

  const submit = async () => {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit(selected);
      onClose();
    } catch (err) {
      setError(err.message || 'That move could not be completed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      width={520}
      title={title}
      subtitle={item ? item.name : undefined}
      icon={
        <div className="stat-card__icon" aria-hidden="true">
          <FolderInput size={16} />
        </div>
      }
      footer={
        <>
          {error && <span className="field__error" style={{ marginRight: 'auto' }}>{error}</span>}
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={busy} disabled={!selected}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 'var(--sp-3)' }}>
        <div className="input-group">
          <Search size={15} />
          <input
            className="input"
            placeholder="Search folders"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        {!query && (
          <div className="crumbs">
            <button type="button" className={cn('link', !parentId && 'is-current')} onClick={() => setParentId(null)}>
              Home
            </button>
            {breadcrumb.map((b) => (
              <span key={b.id} className="row">
                <span className="muted">/</span>
                <button type="button" className="link" onClick={() => setParentId(b.id)}>
                  {b.name}
                </button>
              </span>
            ))}
          </div>
        )}

        <div
          className="card"
          style={{ maxHeight: 300, overflowY: 'auto', padding: 'var(--sp-1)' }}
          role="listbox"
          aria-label="Destination folder"
        >
          {children.length === 0 && (
            <p className="meta" style={{ padding: 'var(--sp-4)', textAlign: 'center' }}>
              {query ? 'No folders match that search.' : 'This folder has no sub-folders.'}
            </p>
          )}
          {children.map((folder) => {
            const isSelf = item && folder.id === item.id;
            return (
              <div
                key={folder.id}
                role="option"
                aria-selected={selected === folder.id}
                className="menu__item"
                style={{
                  background: selected === folder.id ? 'var(--bg-active)' : undefined,
                  color: isSelf ? 'var(--fg-subtle)' : undefined,
                  cursor: isSelf ? 'not-allowed' : 'pointer',
                }}
                onClick={() => !isSelf && setSelected(folder.id)}
                onDoubleClick={() => !isSelf && !query && setParentId(folder.id)}
              >
                <Folder size={15} style={{ color: 'var(--ft-folder)' }} />
                <span className="truncate">{folder.name}</span>
                {!query && !isSelf && (
                  <button
                    type="button"
                    className="btn--icon"
                    style={{ marginLeft: 'auto', width: 24, height: 24 }}
                    aria-label={`Open ${folder.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setParentId(folder.id);
                      setSelected(null);
                    }}
                  >
                    <ChevronRight size={15} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}
